import { PageId } from "../types";
import type { EffectiveSubject } from "../hooks/useEffectiveProtection";

// ENG-2499: single source of truth for hash ↔ PageId, so the hash the app WRITES
// (`#${activePage}` in App.tsx) can always be READ back. The previous
// getInitialPage() was a hand-maintained if-chain with a `return 'home'` default,
// so adding a PageId silently stranded it to Home on cold load / refresh / bookmark
// / post-sign-in redirect (this is exactly how #overview, #report and #org-settings
// regressed).
//
// PAGE_HASHES is exhaustive over PageId — a `Record<PageId, …>` object literal must
// list every PageId, so adding a page WITHOUT wiring its hash is a TypeScript error,
// not a silent runtime drop to Home. The canonical hash of a page is its PageId.
export const PAGE_HASHES: Record<PageId, true> = {
  home: true,
  settings: true,
  users: true,
  groups: true,
  sites: true,
  gmail: true,
  support: true,
  overview: true,
  policy: true,
  extensions: true,
  report: true,
  "saas-inventory": true,
  directory: true,
  domains: true,
  managed: true,
  user: true,
  billing: true,
  "whats-new": true,
  effective: true,
  setup: true,
};

/** Every PageId, at runtime (for tests / iteration). */
export const ALL_PAGE_IDS = Object.keys(PAGE_HASHES) as PageId[];

// Extra inbound hash aliases that resolve to a canonical page (the app never
// WRITES these, but links/redirects use them): `#subscribe` → billing (the desktop
// app's billing deep link; ENG-3479 moved an organization's billing to its own page,
// and anyone outside the org console is sent on to Settings — lib/consoleNav), `#team`
// → users (legacy team link, still in EmailService), `#org-settings` → policy
// (ENG-2513 folded Org Settings into the consolidated Policy surface, so old
// bookmarks/links survive).
export const HASH_ALIASES: Record<string, PageId> = {
  subscribe: "billing",
  team: "users",
  "org-settings": "policy",
};

/**
 * Resolve a raw location hash to a PageId. `hash` must already be stripped of the
 * leading '#' and any `?…` query portion (the Gmail redirect returns
 * `#gmail?gmail_connected=1&email=…`). Unknown or empty hash → 'home'.
 */
export const resolveHashToPage = (hash: string): PageId => {
  if (hash in PAGE_HASHES) return hash as PageId;
  if (hash in HASH_ALIASES) return HASH_ALIASES[hash];
  return "home";
};

/**
 * Parse a raw `window.location.hash` — leading '#', plus any `?…` query the Gmail
 * redirect appends inside the hash — to a PageId. This is the single strip+resolve
 * shared by the cold read (getInitialPage) and the runtime hashchange listener
 * (ENG-2527), so both interpret the URL identically.
 */
export const parseLocationHash = (rawHash: string): PageId =>
  resolveHashToPage(rawHash.replace(/^#/, "").split("?")[0]);

/**
 * The hash the app should be showing for a given page — the WRITER half of this module (ENG-2691).
 *
 * Lived inline in App.tsx's URL-sync effect, which meant the one property this module exists to
 * guarantee — that what the app writes, it can read back — was asserted for the reader and merely
 * hoped for on the writer. It is round-tripped against `parseLocationHash` in the tests now.
 *
 * Home writes an EMPTY hash rather than `#home`, so the canonical URL of the console is the bare
 * origin. `resolveHashToPage` maps both "" and "home" to `home`, so the round trip still holds.
 */
export const hashForPage = (
  page: PageId,
  selectedUserId: string | null,
  policyTab: PolicyTab | null = null,
  effectiveSubject: EffectiveSubject | null = null,
): string => {
  if (page === "home") return "";
  // ENG-2522: the detail view's target rides in the hash query. Without an id there is nothing to
  // show, so it degrades to the bare page rather than writing `#user?id=null`.
  if (page === "user" && selectedUserId) return `#user?id=${selectedUserId}`;
  // ENG-3468: Policy's tab, the same way. No tab is Policy's own default, which depends on what this
  // admin may see, so the bare `#policy` stays the page's canonical URL.
  if (page === "policy" && policyTab) return `#policy?tab=${policyTab}`;
  // ENG-3511: whose browser the page shows, so a member's report can link straight to it. The ids are
  // the opaque membership and group ids, never an email.
  if (page === "effective" && effectiveSubject) return `#effective?${effectiveSubject.kind}=${encodeURIComponent(effectiveSubject.id)}`;
  return `#${page}`;
};

/**
 * ENG-3511: the subject out of a `#effective?member=<id>` or `#effective?group=<id>` hash. Null for any
 * other page, for a bare `#effective`, and for a hash naming neither.
 */
export const parseHashEffectiveSubject = (rawHash: string): EffectiveSubject | null => {
  const hash = rawHash.replace(/^#/, "");
  const [page, query] = hash.split("?");
  if (page !== "effective" || !query) return null;
  const params = new URLSearchParams(query);
  const member = params.get("member");
  if (member) return { kind: "member", id: member };
  const group = params.get("group");
  return group ? { kind: "group", id: group } : null;
};

/**
 * ENG-3468 — Policy's tabs, by the key each has in `#policy?tab=<key>`.
 *
 * All four shared `#policy`, so no tab could be linked to and Back skipped over tab changes. The keys
 * are the ones PolicyPage and every cross-link into it already use, and they are now part of URLs
 * people bookmark, so renaming one breaks those links. `extensions` is not here: ENG-2895 moved that
 * section to its own page, and a link naming it opens Policy's default tab.
 */
export const POLICY_TABS = ["enforcement", "sites", "flags", "notifications", "language"] as const;
export type PolicyTab = (typeof POLICY_TABS)[number];

/** [value] as a Policy tab, or null for anything else — which opens Policy's default tab. */
export const toPolicyTab = (value: string | null | undefined): PolicyTab | null =>
  (POLICY_TABS as readonly string[]).includes(value ?? "") ? (value as PolicyTab) : null;

/**
 * Read the tab out of a `#policy?tab=<key>` hash (ENG-3468), the counterpart of [parseHashUserId].
 * Null for any other page, for a bare `#policy`, and for a key that is not a tab.
 */
export const parseHashPolicyTab = (rawHash: string): PolicyTab | null => {
  const hash = rawHash.replace(/^#/, "");
  const [page, query] = hash.split("?");
  if (page !== "policy" || !query) return null;
  return toPolicyTab(new URLSearchParams(query).get("tab"));
};

/**
 * Read the `id` query param out of a `#user?id=<tenantUserId>` hash (ENG-2522). `parseLocationHash`
 * intentionally strips the `?…`, so the detail view's target id is parsed here instead. Returns null
 * for any other page or a missing id. The id is the opaque tenantUserId (never an email - no PII in
 * the URL). Mirrors the Gmail redirect's in-hash query convention (App.tsx).
 */
export const parseHashUserId = (rawHash: string): string | null => {
  const hash = rawHash.replace(/^#/, "");
  const [page, query] = hash.split("?");
  if (page !== "user" || !query) return null;
  return new URLSearchParams(query).get("id");
};

/**
 * The hash as it was when the bundle loaded, before React ran.
 *
 * App.tsx keeps `window.location.hash` in sync with `activePage`, and that effect fires on the FIRST
 * commit — which strips any `?…` an OAuth provider redirected back with (`#directory?connected=1`
 * becomes `#directory`). Every page that reads its own callback is lazily loaded, so it mounts after
 * that effect and finds the params already gone.
 *
 * Capturing at import time is what makes the read reliable: this module is pulled in by App.tsx, so
 * it evaluates during bundle execution, before the first render.
 */
export const INITIAL_HASH = typeof window !== "undefined" ? window.location.hash : "";

/** Set once the captured hash has been handed to the page it belongs to. */
let initialHashConsumed = false;

/**
 * Take the captured callback hash, once, for the page it addresses.
 *
 * A snapshot alone isn't enough: reading INITIAL_HASH directly in a mount effect replays the banner
 * every time the page remounts, because stripping the live URL can't change a frozen constant. So a
 * provider's "connected!" would reappear each time you navigated back to that tab, hours later.
 *
 * Returns the hash only when its route matches [page] and nothing has claimed it yet — so two pages
 * that both read callbacks (Gmail and the directory) can't steal each other's, and neither can show
 * a stale one twice.
 */
export const consumeInitialHash = (page: PageId): string | null => {
  if (initialHashConsumed) return null;
  const route = INITIAL_HASH.replace(/^#/, "").split("?")[0];
  if (route !== page) return null;
  initialHashConsumed = true;
  return INITIAL_HASH;
};

/** Test-only: forget that the captured hash was consumed. */
export const resetInitialHashForTest = () => {
  initialHashConsumed = false;
};

/**
 * ENG-3376 — each history entry's position, so the console can tell Back from Forward.
 *
 * A popstate says where the browser landed, not how far it moved. To refuse a Back or a Forward and
 * leave history as it was, App.tsx steps the other way by the same distance, so every entry the
 * console makes carries its position in `history.state` under this key. Anything else that rewrites
 * the URL must use `replaceUrl`, which keeps the stamp. An entry that lost it would be taken for one
 * the browser has just added, and a refused Back to it would step the wrong way.
 */
const HISTORY_INDEX_KEY = "havenHistoryIndex";

const asObject = (state: unknown): Record<string, unknown> =>
  state !== null && typeof state === "object" ? (state as Record<string, unknown>) : {};

/** The position stamped on an entry's state, or null for an entry the console did not stamp. */
export const historyIndexOf = (state: unknown): number | null => {
  const index = asObject(state)[HISTORY_INDEX_KEY];
  return typeof index === "number" ? index : null;
};

/** [state] with [index] stamped on it. */
export const withHistoryIndex = (state: unknown, index: number): Record<string, unknown> => ({
  ...asObject(state),
  [HISTORY_INDEX_KEY]: index,
});

/** Rewrite the current entry's URL, to drop a callback's params say, keeping its position stamp. */
export const replaceUrl = (url: string): void => {
  window.history.replaceState(window.history.state, "", url);
};

/** Where the browser lands after the Google consent round-trip (ENG-2586). */
export interface DirectoryCallback {
  connected: boolean;
  /** The backend's machine-readable failure code, e.g. `not_a_workspace_account`. */
  error: string | null;
}

/**
 * Read the directory OAuth callback out of a `#directory?connected=1` / `#directory?error=<code>`
 * hash. This contract is the BACKEND's: `GET /business/directory/callback` redirects to exactly
 * these URLs (`PremiumRoutes.kt`, the `$portal/#directory?…` redirects), which is why the console's
 * hash for this page cannot be renamed without changing the backend too.
 *
 * Returns null for any other page, so a caller can tell "not a callback" from "a callback that
 * failed" — they need different UI.
 */
export const parseDirectoryCallback = (rawHash: string): DirectoryCallback | null => {
  const hash = rawHash.replace(/^#/, "");
  const [page, query] = hash.split("?");
  if (page !== "directory" || !query) return null;
  const params = new URLSearchParams(query);
  const error = params.get("error");
  const connected = params.get("connected") === "1";
  if (!error && !connected) return null;
  return { connected, error };
};

/**
 * Whether the URL asks for the LOG-IN form rather than the sign-up form (ENG-2734).
 *
 * The unauthenticated app serves both from `/`, switching between them client-side, so there was no
 * way to link anyone straight to log-in. The extension's signed-out reminder needs one: its
 * secondary CTA is "I already have one", and without this it would drop an existing user on the
 * signup screen.
 *
 * A QUERY param, not a hash: the hash namespace belongs to `PageId` (see PAGE_HASHES above), and
 * this is a pre-auth view rather than a dashboard page. Anything other than `view=login` — absent,
 * empty, `view=signup`, a typo — leaves the default sign-up view alone.
 */
export const wantsLoginView = (search: string = window.location.search): boolean =>
  new URLSearchParams(search).get("view") === "login";
